'use client';

import { VisuallyHidden } from '@ariakit/react';
import { getClassNames } from '@websolutespa/bom-core';
import { useIntersectionObserver, useMounted, useReducedMotionLocal } from '@websolutespa/bom-mixer-hooks';
import { AnimationItem } from 'lottie-web';
import React, { MutableRefObject, forwardRef, useCallback, useEffect, useId, useImperativeHandle, useRef, useState } from 'react';
import LottiePlayerLight from 'react-lottie-player/dist/LottiePlayerLight';
import styled from 'styled-components';
import { Button } from '../button/button';
import { UIStyledComponentProps } from '../types';
import { getCssResponsive } from '../utils';

const SvgAnimationStrategy = {
  Default: 'default', // autoplay
  Once: 'once',
  Scroll: 'scroll',
  Over: 'over',
  OverOut: 'overOut',
  Controlled: 'controlled',
} as const;

type SvgAnimationStrategy = typeof SvgAnimationStrategy[keyof typeof SvgAnimationStrategy];

type Props = {
  href?: string;
  animation?: () => Promise<object>;
  mode?: SvgAnimationStrategy;
  loop?: boolean;
  scrollableRef?: MutableRefObject<Element | undefined>;
};

export type SvgAnimationProps = UIStyledComponentProps<Props>;

export type SvgAnimationRef = {
  container: HTMLDivElement | null,
  play: () => void,
};

// export type SvgAnimationComponent<C extends React.ElementType = 'div'> = UIComponentWithRef<C, Props>;

const StyledSvgAnimation = styled.div<SvgAnimationProps>`
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
  background: transparent;
  & > div {
    object-fit: contain;
    width: 100%;
    height: 100%;
  }
  ${props => getCssResponsive(props)}
`;

const StyledButton = styled(Button)`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 1;
`;

export const SvgAnimation = forwardRef<SvgAnimationRef, SvgAnimationProps>(({
  className,
  href,
  animation,
  mode = SvgAnimationStrategy.Default,
  loop = false,
  scrollableRef,
  width,
  height,
  ...props
}, forwardedRef) => {
  if (!href && !animation) {
    throw 'SvgAnimation.error: please submit an animation loader or a href';
  }
  const elementRef = useRef<HTMLDivElement>(null);
  const isIntersecting = useIntersectionObserver(elementRef, {
    threshold: mode === SvgAnimationStrategy.Once ? 0.5 : 0,
    root: null,
    rootMargin: '0px',
  });
  const playerRef = useRef<AnimationItem>(undefined);
  const [shouldLoad, setShouldLoad] = useState<boolean>(false);
  const [canPlay, setCanPlay] = useState<boolean>(false);
  const [animationData, setAnimationData] = useState<object>();
  const [animationPath, setAnimationPath] = useState<string>();
  const [animationPosition, setAnimationPosition] = useState<number>();

  const mounted = useMounted();
  const id = useId();
  const { reducedMotionLocal, toggleReducedMotionLocal, setReducedMotionLocal } = useReducedMotionLocal();
  const shouldPlay = mounted && canPlay && !reducedMotionLocal;

  /*
  const play = useCallback(() => {
    if (!playerRef.current) {
      return;
    }
    switch (mode) {
      case SvgAnimationStrategy.Scroll:
        return;
      default:
        // console.log(playerRef.current.currentFrame, playerRef.current.totalFrames, playerRef.current.isPaused);
        if (playerRef.current.isPaused) {
          playerRef.current.goToAndPlay(0);
        }
    }
  }, [playerRef, mode]);

  const pause = useCallback(() => {
    if (!playerRef.current) {
      return;
    }
    switch (mode) {
      case SvgAnimationStrategy.Scroll:
        return;
      default:
        playerRef.current.pause();
    }
  }, [playerRef, mode]);

  // handle shouldPlay
  useEffect(() => {
    if (shouldPlay) {
      play();
    } else {
      pause();
    }
  }, [shouldPlay, play]);
  */

  const enter = useCallback(() => {
    if (!playerRef.current) {
      return;
    }
    // console.log(playerRef.current.currentFrame);
    switch (mode) {
      case SvgAnimationStrategy.Default:
        if (shouldPlay) {
          playerRef.current.goToAndPlay(0);
        } else {
          playerRef.current.goToAndStop(0);
        }
        break;
      case SvgAnimationStrategy.Once:
        if (shouldPlay && playerRef.current.currentFrame === 0) {
          playerRef.current.goToAndPlay(0);
        }
        break;
      case SvgAnimationStrategy.Over:
        playerRef.current.goToAndStop(playerRef.current.totalFrames - 1, true);
        break;
      case SvgAnimationStrategy.OverOut:
        playerRef.current.goToAndStop(0, true);
        break;
    }
    // console.log('onLoad', playerRef.current);
  }, [mode, shouldPlay]);

  const exit = useCallback(() => {
    if (!playerRef.current) {
      return;
    }
    if (mode === SvgAnimationStrategy.Default) {
      playerRef.current.stop();
    }
  }, [mode]);

  // handle intersection
  useEffect(() => {
    if (isIntersecting) {
      // on enter
      if (!shouldLoad) {
        setShouldLoad(true);
      } else {
        enter();
      }
    } else {
      // on exit
      exit();
    }
  }, [isIntersecting, enter, exit, shouldLoad, shouldPlay]);

  // load animation on shouldLoad
  useEffect(() => {
    if (shouldLoad) {
      // console.log('SvgAnimation.shouldLoad');
      if (typeof animation === 'function') {
        animation().then(setAnimationData);
      } else {
        setAnimationPath(href);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldLoad, href]);

  // handle scroll
  useEffect(() => {
    const scrollElement = mode === SvgAnimationStrategy.Scroll ? (scrollableRef ? scrollableRef.current : window) : undefined;
    const onScroll = () => {
      if (elementRef.current && playerRef.current) {
        let top: number = 0;
        const rect = elementRef.current.getBoundingClientRect();
        if (scrollableRef && scrollableRef.current) {
          const scrollableRect = scrollableRef.current.getBoundingClientRect();
          top = 1 - ((rect.top - scrollableRect.top) / (scrollableRect.height + rect.height));
        } else {
          top = 1 - (rect.top / (window.innerHeight + rect.height));
        }
        const position = Math.max(0, Math.min(1, top));
        const frame = Math.round(playerRef.current.totalFrames * position);
        setAnimationPosition(frame);
      }
    };
    if (mode === SvgAnimationStrategy.Scroll) {
      if (scrollElement) {
        scrollElement.addEventListener('scroll', onScroll, { passive: true });
      }
    }
    return () => {
      if (mode === SvgAnimationStrategy.Scroll && scrollElement) {
        scrollElement.removeEventListener('scroll', onScroll);
      }
    };
  }, [mode, scrollableRef]);

  useImperativeHandle(forwardedRef, () => {
    return {
      container: elementRef.current,
      play: () => {
        if (!playerRef.current) {
          return;
        }
        // console.log(playerRef.current.currentFrame, playerRef.current.totalFrames, playerRef.current.isPaused);
        if (playerRef.current.isPaused) {
          playerRef.current.goToAndPlay(0);
        }
      },
    };
  }, [elementRef]);

  const onLoad = () => {
    setCanPlay(true);
  };

  const over = () => {
    if (!playerRef.current) {
      return;
    }
    switch (mode) {
      case SvgAnimationStrategy.Over:
        playerRef.current.goToAndPlay(0);
        // console.log('over.SvgAnimationStrategy.Over');
        break;
      case SvgAnimationStrategy.OverOut:
        playerRef.current.setDirection(1);
        playerRef.current.setSpeed(1);
        if (playerRef.current.currentFrame === playerRef.current.totalFrames - 1) {
          playerRef.current.goToAndPlay(0);
        } else {
          playerRef.current.play();
        }
        // console.log('over.SvgAnimationStrategy.OverOut');
        break;
    }
  };

  const out = () => {
    if (!playerRef.current) {
      return;
    }
    // console.log('out', canPlay);
    switch (mode) {
      case SvgAnimationStrategy.Over:
        // playerRef.current.goToAndStop(playerRef.current.totalFrames - 1, true);
        break;
      case SvgAnimationStrategy.OverOut:
        playerRef.current.setDirection(-1);
        playerRef.current.setSpeed(1.5);
        playerRef.current.play();
        break;
    }
  };

  const onClick = (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    switch (mode) {
      case SvgAnimationStrategy.Over:
      case SvgAnimationStrategy.OverOut:
        return;
      default:
        toggleReducedMotionLocal();
    }
  };

  const onFocus = (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    over();
  };

  const onBlur = (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    setReducedMotionLocal(true);
    out();
  };

  const onMouseEnter = (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    if (typeof props.onMouseEnter === 'function') {
      props.onMouseEnter(event);
    }
    if (!shouldPlay) {
      return;
    }
    over();
  };

  const onMouseLeave = (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    if (typeof props.onMouseLeave === 'function') {
      props.onMouseLeave(event);
    }
    if (!shouldPlay) {
      return;
    }
    out();
  };

  const classNames = getClassNames('lottie', className, { loading: !animationData });
  return (
    <StyledSvgAnimation ref={elementRef} className={classNames} {...props}>
      {mounted && (
        <StyledButton
          aria-controls={id}
          aria-pressed={!reducedMotionLocal}
          onClick={onClick}
          onBlur={onBlur}
          onFocus={onFocus}
          onMouseEnter={onMouseEnter}
          onMouseLeave={onMouseLeave}
        >
          <VisuallyHidden>
            {reducedMotionLocal ? 'Play' : 'Pause'}
          </VisuallyHidden>
        </StyledButton>
      )}
      {(animationData || animationPath) && (
        <LottiePlayerLight
          ref={playerRef}
          id={id}
          {...(animationData ? { animationData } : { path: animationPath })}
          goTo={animationPosition}
          onLoad={onLoad}
          loop={loop}
          style={{ width, height }} />
      )}
    </StyledSvgAnimation>
  );
});

SvgAnimation.displayName = 'SvgAnimation';
